import { useState } from "react";
import { Paper, Box, Typography, Avatar, Button } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import SyncIcon from "@mui/icons-material/Sync";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import { useGetGlobalProductInformation } from "../../../../client/hooks/products/products.queries";
import { useImportCatalog } from "../../../../client/hooks/products/products.mutations";
import { useEffect } from "react";

const STORAGE_KEY = "lastSyncCatalogTimestamp";
const THREE_HOURS_MS = 3 * 60 * 60 * 1000;

const CatalogStatusBar = () => {
  const { mutate, isLoading: isSyncing } = useImportCatalog();
  const [isDisabled, setIsDisabled] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);

  const { data: globalData, isLoading } = useGetGlobalProductInformation();

  useEffect(() => {
    const lastSync = localStorage.getItem(STORAGE_KEY);
    if (lastSync) {
      const diff = Date.now() - parseInt(lastSync);
      if (diff < THREE_HOURS_MS) {
        setIsDisabled(true);
        setRemainingTime(THREE_HOURS_MS - diff);
      }
    }
  }, []);

  useEffect(() => {
    if (!isDisabled) return;

    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1000) {
          setIsDisabled(false);
          localStorage.removeItem(STORAGE_KEY);
          clearInterval(interval);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isDisabled]);

  // Função para formatar valor monetário
  function formatCurrency(value, currency = "BRL") {
    if (typeof value !== "number") return "-";
    return value.toLocaleString("pt-BR", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    });
  }

  // Loading visual para status bar
  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          bgcolor: "#f7f7ff",
          border: "1px solid #e0e0ff",
          borderRadius: 2,
          minHeight: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Carregando informações do catálogo...
        </Typography>
      </Paper>
    );
  }

  const formatTime = (ms) => {
    const total = Math.floor(ms / 1000);
    const h = String(Math.floor(total / 3600)).padStart(2, "0");
    const m = String(Math.floor((total % 3600) / 60)).padStart(2, "0");
    const s = String(total % 60).padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  function handleSyncCatalog() {
    mutate(undefined, {
      onSuccess: () => {
        const now = Date.now();
        localStorage.setItem(STORAGE_KEY, now.toString());
        setIsDisabled(true);
        setRemainingTime(THREE_HOURS_MS);
      },
    });
  }

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 3,
        p: 2,
        bgcolor: "#f7f7ff",
        border: "1px solid #e0e0ff",
        borderRadius: 2,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "flex-start", md: "center" },
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar sx={{ bgcolor: "#5c6bc0", width: 40, height: 40 }}>
          <DashboardIcon />
        </Avatar>
        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="#3f51b5"
            letterSpacing={0.5}
          >
            Status do Catálogo
          </Typography>
          <Typography variant="body2" color="#6c6f93" fontWeight={500}>
            Última sincronização:{" "}
            {globalData?.lastSync && globalData.lastSync !== "-"
              ? (() => {
                  const d = new Date(globalData.lastSync);
                  return `${d.toLocaleDateString("pt-BR")} às ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
                })()
              : "-"}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          ml: { xs: 0, md: "auto" },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box
            sx={{
              minWidth: 120,
              px: 1,
              py: 0.5,
              background: "linear-gradient(90deg, #e3e6fa 0%, #f7f7ff 100%)",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Inventory2OutlinedIcon
              sx={{ color: "#5c6bc0", fontSize: 22, mr: 0.5 }}
            />
            <Box>
              <Typography
                variant="caption"
                color="#5c6bc0"
                fontWeight={700}
                letterSpacing={0.5}
                textTransform="uppercase"
                sx={{ fontSize: 13 }}
              >
                Produtos Ativos
              </Typography>
              <Typography
                variant="h6"
                fontWeight={800}
                color="#3f51b5"
                fontSize={22}
                lineHeight={1.1}
              >
                {globalData?.activeProducts != null
                  ? globalData.activeProducts
                  : "-"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              minWidth: 120,
              px: 1,
              py: 0.5,
              background: "linear-gradient(90deg, #eafaf1 0%, #f7f7ff 100%)",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <StorageOutlinedIcon
              sx={{ color: "#388e3c", fontSize: 22, mr: 0.5 }}
            />
            <Box>
              <Typography
                variant="caption"
                color="#388e3c"
                fontWeight={700}
                letterSpacing={0.5}
                textTransform="uppercase"
                sx={{ fontSize: 13 }}
              >
                Estoque Total
              </Typography>
              <Typography
                variant="h6"
                fontWeight={800}
                color="#388e3c"
                fontSize={22}
                lineHeight={1.1}
              >
                {globalData?.totalStock != null ? globalData.totalStock : "-"}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              minWidth: 140,
              px: 1,
              py: 0.5,
              background: "linear-gradient(90deg, #e3fae6 0%, #f7f7ff 100%)",
              borderRadius: 1,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <MonetizationOnOutlinedIcon
              sx={{ color: "#388e3c", fontSize: 22, mr: 0.5 }}
            />
            <Box>
              <Typography
                variant="caption"
                color="#388e3c"
                fontWeight={700}
                letterSpacing={0.5}
                textTransform="uppercase"
                sx={{ fontSize: 13 }}
              >
                Valor do Catálogo
              </Typography>
              <Typography
                variant="h6"
                fontWeight={800}
                color="#388e3c"
                fontSize={22}
                lineHeight={1.1}
              >
                {globalData?.catalogValue != null
                  ? formatCurrency(globalData.catalogValue, globalData.currency)
                  : "-"}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Button
          variant="outlined"
          startIcon={<SyncIcon />}
          onClick={handleSyncCatalog}
          disabled={isDisabled || isSyncing}
          sx={{
            borderColor: "#5c6bc0",
            color: "#5c6bc0",
            "&:hover": {
              bgcolor: "rgba(92, 107, 192, 0.08)",
              borderColor: "#3f51b5",
            },
          }}
        >
          {isDisabled ? `Aguarde: ${formatTime(remainingTime)}` : "Sincronizar"}
        </Button>
      </Box>
    </Paper>
  );
};

export default CatalogStatusBar;
